import React, { useEffect, useState } from "react";

import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import _ from 'lodash';

import { invalidateCollectionUpdateState } from "src/redux/actions/collections";
import { useSelector } from "react-redux";
import { BENTO_EXPERIENCE, CAROUSEL_EXPERIENCE, METRO_EXPERIENCE } from "src/constants/collections";
import { BentoExperience } from "src/pages/collection/experiences/bento";
import { CarouselExperience } from "src/pages/collection/experiences/carousel";
import { MetroExperience } from "src/pages/collection/experiences/metro";
import { Menubar, MenuState } from "src/pages/collection/components/menu";
import { CollectionAsset, CollectionChangeRequest } from "src/types/collections";
import { calculateUpdateConversationRequest, shouldNotifyOnChange } from "src/utils/collections.helper";
import Reveal from "src/components/animation";
import { AddAssetsDialog } from "src/components/asset/add";

import styles from './edit.module.scss';
import { addAssetToConversation, createSection, editConversation, fetchAssetsForConversation, fetchConversation, fetchConversationSection, fetchRecipientsForConversation, invalidateConversation, invalidateEditConversation, moveAsset, moveAssetToSection, moveSection, removeAssetFromConversation, removeSection, renameSection, updateAssetMetadata, updateConversation, updateConversationProperty } from "src/redux/actions/conversation";
import { CollectionContext } from "src/pages/collection/components/context";
import { NotifyChangesDialog } from "src/pages/collection/components/live";
import { startBackgroundUploadTask } from "src/redux/actions/backgroundTask";
import { Asset, AssetUpload } from "src/types/assets";

const EditConversationPage: React.FC<object> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id, sectionId } = useParams();

  const [menuState, setMenuState] = React.useState<MenuState>({
    primaryAction: 'edit',
  });

  const [notifyRecipients, setNotifyRecipients] = useState<boolean>(false);

  const user = useSelector((state: any) => _.get(state, 'users.me'));
  const loading = useSelector((state: any) => _.get(state, `conversations.conversation['${id}'].loading`, false));
  const loaded = useSelector((state: any) => _.get(state, `conversations.conversation['${id}'].loaded`, false));

  const loadingAssets = useSelector((state: any) => _.get(state, `conversations.conversation['${id}'].loadingAssets`, false));
  const loadedAssets = useSelector((state: any) => _.get(state, `conversations.conversation['${id}'].loadedAssets`, false));

  const persistedConversation = useSelector((state: any) => {
    return _.get(state, `conversations.conversation['${id}'].item`);
  });

  const conversation = useSelector((state: any) => {
    const editCollection = _.get(state, `conversations.edit`);
    if (editCollection) {
      return editCollection;
    }

    return _.get(state, `conversations.conversation['${id}'].item`);
  });

  const updated = useSelector((state: any) => _.get(state, `conversations.update['${id}'].updated`, false));

  const experienceType = _.get(conversation, 'viewerType');
  const editing = menuState.primaryAction !== 'edit';

  const paperfliteEmailDisabled = useSelector((state: any) => {
    const paperfliteEmailServerEnabled = _.get(state, 'account.item.paperfliteEmailServerEnabled', true);
    return !paperfliteEmailServerEnabled;
  });

  useEffect(() => {
    if (id) {
      dispatch(fetchConversation(id));
    }

    return () => {
      dispatch(invalidateEditConversation(id!));
      dispatch(invalidateConversation());
    }
  }, [id]);

  useEffect(() => {
    if (loaded) {
      dispatch(editConversation(conversation));
      dispatch(fetchAssetsForConversation({
        id: id!,
        type: 'asset'
      }));
    }
  }, [loaded]);

  useEffect(() => {
    if (loadedAssets) {
      dispatch(fetchRecipientsForConversation({
        id: id!,
        page: 0,
        sortBy: 'activity_date',
        orderBy: 'DESC',
        filter: 'known'
      }));
    }
  }, [loadedAssets])

  useEffect(() => {
    if (loadedAssets) {
      const sections = _.size(_.get(persistedConversation, 'sections', []));
      if (sections == 0) {
        if (sectionId) {
          navigate(`/conversations/${id}/edit`, {
            replace: true
          });
        }

        return;
      }

      let sectionIndex = 0;
      if (sectionId && sections > 0) {
        sectionIndex = parseInt(sectionId);
        if (sectionIndex < 0 || sectionIndex >= sections) {
          sectionIndex = 0;
        }
      }

      navigate(`/conversations/${id}/sections/${sectionIndex}/edit`, {
        replace: true
      });
    }
  }, [loadedAssets]);

  useEffect(() => {
    if (updated && menuState.primaryAction === 'edit') {
      // setMenuState({ primaryAction: 'view' });
      // dispatch(invalidateEditCollection(id!));
      dispatch(invalidateCollectionUpdateState());
    }
  }, [menuState.primaryAction, updated]);

  useEffect(() => {
    switch (menuState.secondaryAction) {
      case 'save': {
        handleSave();
        setMenuState(state => ({ ...state, secondaryAction: null }));
        break;
      }
      case 'reset': {
        if (menuState.primaryAction === 'edit') {
          dispatch(invalidateEditConversation(id!));
          handleBack();
          setMenuState(state => ({ ...state, secondaryAction: null }));
        }

        break;
      }
      default:
        break;
    }
  }, [menuState.secondaryAction]);

  useEffect(() => {
    setCollectionContextState(calculateCollectionContextState());
  }, [conversation]);

  const calculateCollectionContextState = () => {
    return {
      id: id,
      entityType: 'conversation',
      itemSelector: `conversations.conversation['${id}'].item`,
      editSelector: `conversations.edit`,
      updateSelector: `conversations.update['${id}']`,
      recipientSelector: `conversations.conversation['${id}'].recipients`,
      fetchSection: handleFetchSection,
      onSectionClick: handleSectionClick,
      createSection: handleAddSection,
      removeSection: handleRemoveSection,
      renameSection: handleRenameSection,
      moveSection: handleMoveSection,

      removeAsset: handleRemoveAsset,
      moveAsset: handleAssetMove,
      moveAssetToSection: handleAssetMoveToSection,

      updateAssetMetadata: handleUpdateAssetMetadata,
      updateCollectionProperty: handleUpdateCollectionProperty,

      getRecipient: handleGetRecipient,
      fetchRecipientsForPage: handleFetchRecipientsForPage,

      getNotificationContent: getNotificationContentForConversation
    }
  }

  const validateSectionIndex = (index: number, sections: number) => {
    if (sections == 0) {
      return undefined;
    } else if (index < 0 || index >= sections) {
      return "0";
    } else {
      return "" + index;
    }
  }

  const handleMenuStateChange = (value) => {
    setMenuState(value);
  }

  // Section Actions
  const handleFetchSection = (section: string) => {
    dispatch(fetchConversationSection({
      conversation: id!,
      section: section
    }))
  }

  const handleSectionClick = (section: any) => {
    navigate(`/conversations/${conversation.id}/sections/${section}/edit`);
  }

  const handleAddSection = (id: string, name: string) => {
    dispatch(createSection({
      conversation: id,
      name: name
    }));
  }

  const handleRemoveSection = (section: number) => {
    dispatch(removeSection({
      conversation: conversation.id!,
      index: section,
    }));
  }

  const handleRenameSection = (section: number, name: string) => {
    dispatch(renameSection({
      conversation: conversation.id!,
      index: section,
      name: name
    }));
  }

  const handleMoveSection = (from: number, to: number) => {
    dispatch(moveSection({
      conversation: id!,
      from: from,
      to: to
    }));
  }

  const handleRemoveAsset = (section: number | null, asset: string) => {
    dispatch(removeAssetFromConversation({
      conversation: conversation.id!,
      section: section ?? undefined,
      asset: asset
    }));
  }

  const handleAssetMove = (section: number, from: number, to: number) => {
    dispatch(moveAsset({
      conversation: id!,
      section: section ?? undefined,
      from: from,
      to: to
    }));
  }

  const handleAssetMoveToSection = (source: number, target: number, asset: CollectionAsset) => {
    dispatch(moveAssetToSection({
      conversation: id!,
      source: source,
      target: target,
      asset: asset
    }));
  }

  const handleUpdateAssetMetadata = (section: number | null, updates: { id: string; metadata: any; }[]) => {
    dispatch(updateAssetMetadata({
      conversation: id!,
      section: section ?? undefined,
      updates: updates
    }));
  }

  const handleUpdateCollectionProperty = (property: string, value: any) => {
    dispatch(updateConversationProperty({
      conversation: conversation.id!,
      property: property,
      value: value
    }));
  }

  const handleSave = () => {
    const shouldNotify = shouldNotifyOnChange(persistedConversation, conversation);
    if (shouldNotify && !paperfliteEmailDisabled) {
      setNotifyRecipients(true);
    } else {
      handleSaveConfirm(false, '', '');
    }
  }

  const handleNotifyRecipientConfirmation = (notifyRecipients, subject, message) => {
    handleSaveConfirm(notifyRecipients, subject, message);
  }

  const handleSaveConfirm = (notifyRecipients, subject, message) => {
    const changes: CollectionChangeRequest = calculateUpdateConversationRequest(persistedConversation, conversation);

    changes.notifyRecipients = notifyRecipients;
    changes.subject = subject;
    changes.message = message;

    dispatch(updateConversation({
      id: conversation.id,
      request: changes
    }));
  }

  const handleBack = () => {
    navigate(`/conversations/${id}`);
  }

  const handleCloseAddAsset = () => {
    setMenuState({
      ...menuState,
      secondaryAction: null
    });
  }

  const handleAddAssetFinish = (entities: any[]) => {
    if (_.size(entities) > 0) {
      const uploading = entities.filter(item => item.entityType == 'asset-upload');
      if (!_.isEmpty(uploading)) {
        const assest = entities.filter(item => _.includes(['asset', 'asset-upload'], item.entityType));
        dispatch(startBackgroundUploadTask({
          assets: assest as [Asset | AssetUpload],
          entity: {
            id: conversation.id!,
            entityType: 'share',
            name: conversation.subject,
            url: `/conversations/${conversation.id}`,
            section: sectionId
          }
        }));
      } else {
        const assets = entities.filter(item => item.entityType == 'asset');
        dispatch(addAssetToConversation({
          conversation: conversation.id,
          section: sectionId ? parseInt(sectionId) : undefined,
          assets: assets.map(item => ({ id: item.id, asset: item } as CollectionAsset)),
          addAt: "end"
        }));
      }
    }

    handleCloseAddAsset();
  }

  const handleGetRecipient = (recipient) => {
    const firstName = _.get(recipient, 'contact.firstName');
    const lastName = _.get(recipient, 'contact.lastName');
    const email = _.get(recipient, 'contact.email');
    const company = _.get(recipient, 'contact.company');
    const sharedDate = _.get(recipient, 'sharedDate');

    return {
      firstName: firstName,
      lastName: lastName,
      email: email,
      company: company,
      sharedDate: sharedDate
    }
  }

  const handleFetchRecipientsForPage = (page) => {
    dispatch(fetchRecipientsForConversation({
      id: id!,
      page: page,
      sortBy: 'activity_date',
      orderBy: 'DESC',
      filter: 'known'
    }));
  }

  const getNotificationContentForConversation = () => {
    return {
      subject: `${conversation?.subject || ""} was updated`,
      message: `<span>Hello there,</span><br/><br/>We've recorded some changes to the content collection
      ${conversation?.subject || ""} that was shared with you by <a href='mailto:${user.email}'>${user.email}</a>.<br/><br/>`
    }
  }

  // Conversation State
  const [collectionContextState, setCollectionContextState] = useState<any>(calculateCollectionContextState());

  if ((loading || loadingAssets) && !conversation) {
    return <div className={styles.loading}>Loading conversation...</div>
  }

  if (!conversation) {
    return <div>No Conversation</div>
  }

  return (
    <CollectionContext.Provider value={collectionContextState}>
      <div className={styles.container}>
        <Reveal className={styles.backButtonContainer} show={!editing}>
          <button className={styles.backButton} onClick={handleBack}>
            <i className="paperflite-arrow-up"></i>
          </button>
        </Reveal>
        <div className={styles.menubar}>
          <Menubar
            collection={conversation}
            menuState={menuState}
            onChange={handleMenuStateChange}
          />

        </div>
        <div className={styles.experience}>
          {experienceType == BENTO_EXPERIENCE &&
            <BentoExperience
              collection={conversation}
              menuState={menuState}
              onMenuChange={handleMenuStateChange}
            />
          }

          {experienceType == CAROUSEL_EXPERIENCE &&
            <CarouselExperience
              collection={conversation}
              menuState={menuState}
              onMenuChange={handleMenuStateChange}
            />
          }

          {experienceType == METRO_EXPERIENCE &&
            <MetroExperience
              collection={conversation}
              menuState={menuState}
              onMenuChange={handleMenuStateChange}
            />
          }

          {menuState.secondaryAction == 'add-asset' &&
            <AddAssetsDialog show={menuState.secondaryAction == 'add-asset'}
              selectableEntityTypes={['asset']}
              onFinish={handleAddAssetFinish}
              onClose={handleCloseAddAsset}
            />
          }
        </div>
      </div>

      <NotifyChangesDialog
        show={notifyRecipients}
        collection={conversation}
        onConfirm={handleNotifyRecipientConfirmation}
        onClose={() => setNotifyRecipients(false)}
      />
    </CollectionContext.Provider>
  );
}

export { EditConversationPage };